import * as THREE from "three"
import {
  buildWallRunIndex,
  carvedVoxelKey,
  carvedVoxels,
  cellFaceKey,
  cellFloorDepth,
  cellStackBaseY,
  cellWorldHeight,
  digTunnelLength,
  FACE_GRID,
  faceTileGrid,
  openingDigThrough,
  parseCellFaceKey,
  applyWallTileColors,
  sectorSizeMeters,
  wallFaceRowCount,
  wallRunSurfaceMeshData,
  type CellFace,
  type FaceTile,
  type SceneCell,
  type SceneOpening,
  type ScenePlatform,
  type WallRunIndex,
} from "@/lib/location-scene-3d/platform"
import { sceneFaceRecordKey, type SceneFaceState, type SceneLightState } from "@/lib/location-scene-3d/scene-state"
import { GRID_MAJOR_EVERY, GRID_MINOR_WIDTH_OF_CELL, sceneThemeById } from "@/lib/location-scene-3d/scene-theme"
import {
  sectorGridY,
  selectCapHeight,
  selectCapLift,
  selectCapSeat,
  selectPaintLift,
  selectionToolbarPad,
  wallOverlayGap,
} from "@/lib/location-scene-3d/scene-scale"
import { applyHeightFade, hexToNumber, PALETTE as LIGHT_PALETTE } from "@/lib/location-scene-3d/light-palette"

export type PlatformLightSide = "north" | "east" | "south" | "west" | "top"
export type PlatformTool = "sector" | "surface" | null

export type PlatformLayerState = {
  platform: ScenePlatform | null
  cells: SceneCell[]
  faces: SceneFaceState[]
  openings: SceneOpening[]
  selectedSectors: { x: number; z: number }[]
  selectedFaceTiles: FaceTile[]
  activeFaceKey: string | null
  light: SceneLightState
  surfaceGrid: boolean
}

export type PlatformHit =
  | { kind: "sector"; x: number; z: number; face: CellFace }
  | { kind: "face"; faceKey: string }
  | { kind: "opening"; faceKey: string; u: number; v: number }

const CELL_IDLE = hexToNumber(LIGHT_PALETTE.tile)
const CELL_SIDE = hexToNumber(LIGHT_PALETTE.wall)
const CELL_SELECTED = 0xf5c16c
/**
 * Functional sector-grid / box-edge outline — deliberately NOT a palette token. The palette's
 * surface.seam is intentionally near-invisible (quiet tile grout); this overlay must stay
 * legible for editing regardless of the active palette.
 */
const TOP_ROUGHNESS = 0.85
const SIDE_ROUGHNESS = 0.92
const FACE_ORDER: CellFace[] = ["east", "west", "top", "bottom", "south", "north"]
// InstancedMesh per-instance color is a multiply against the material's own base color/map.
// flatFloorMats already carry their true colors (white+map on top, flat CELL_IDLE on the sides) —
// tinting idle instances with CELL_IDLE again would square it (albedo*albedo), reading as dull/dark
// next to the un-instanced cell meshes that carry no such multiply. Idle tint must stay neutral.
const COLOR_IDLE = new THREE.Color(0xffffff)

export function lightPosition(side: PlatformLightSide, span: number): [number, number, number] {
  const y = span * 0.45
  if (side === "north") return [0, y, -span]
  if (side === "east") return [span, y, 0]
  if (side === "south") return [0, y, span]
  if (side === "west") return [-span, y, 0]
  return [0, span * 0.7, 0]
}

/** New-scene lamp, as a fraction of span. At 32 m this is the corner-camera marker [5.67, 11.69, 14.62]. */
const DEFAULT_LIGHT_SPAN_FRACTION = [0.177, 0.365, 0.457] as const

export function defaultLightPosition(span: number): [number, number, number] {
  const s = Number.isFinite(span) && span > 0 ? span : 32
  return [s * DEFAULT_LIGHT_SPAN_FRACTION[0], s * DEFAULT_LIGHT_SPAN_FRACTION[1], s * DEFAULT_LIGHT_SPAN_FRACTION[2]]
}

function faceFromNormal(normal: THREE.Vector3): CellFace {
  const ax = Math.abs(normal.x)
  const ay = Math.abs(normal.y)
  const az = Math.abs(normal.z)
  if (ay >= ax && ay >= az) return normal.y >= 0 ? "top" : "bottom"
  if (ax >= az) return normal.x >= 0 ? "east" : "west"
  return normal.z >= 0 ? "south" : "north"
}

function geometryKey(state: PlatformLayerState): string {
  const platform = state.platform
  if (!platform) return "none"
  const cells = [...state.cells]
    .sort((a, b) => a.z - b.z || a.x - b.x)
    .map((cell) => `${cell.x}:${cell.z}:${cell.height}`)
    .join("|")
  const openings = [...state.openings]
    .sort((a, b) => a.face_key.localeCompare(b.face_key) || a.v - b.v || a.u - b.u)
    .map((opening) => `${opening.face_key}:${opening.u}:${opening.v}:${opening.depth}`)
    .join("|")
  return [platform.sectors_x, platform.sectors_z, platform.sector_size, platform.unit, cells, openings].join(";")
}

function facesKey(state: PlatformLayerState): string {
  return [...state.faces]
    .sort((a, b) => a.face_key.localeCompare(b.face_key))
    .map((face) => sceneFaceRecordKey(face))
    .join("|")
}

function selectionKey(state: PlatformLayerState): string {
  const sectors = state.selectedSectors
    .map((sector) => `${sector.x}:${sector.z}`)
    .sort()
    .join("|")
  const tiles = state.selectedFaceTiles
    .map((tile) => `${tile.faceKey}:${tile.u}:${tile.v}`)
    .sort()
    .join("|")
  return `${state.activeFaceKey ?? ""}#${sectors}#${tiles}#${state.surfaceGrid ? 1 : 0}`
}

function clampIndex(value: number, maxExclusive: number) {
  if (maxExclusive <= 1) return 0
  return Math.max(0, Math.min(maxExclusive - 1, value))
}

function uvFromPointOnFace(
  face: CellFace,
  point: THREE.Vector3,
  cx: number,
  cz: number,
  total: number,
  sectorM: number,
  baseY: number,
): { u: number; v: number } {
  if (face === "top" || face === "bottom") {
    const step = sectorM / FACE_GRID
    const u = clampIndex(Math.floor((point.x - (cx - sectorM / 2)) / step), FACE_GRID)
    const v = clampIndex(Math.floor((point.z - (cz - sectorM / 2)) / step), FACE_GRID)
    return { u, v }
  }
  const { cols, rows, step } = faceTileGrid(total, sectorM)
  const v = clampIndex(Math.floor((point.y - baseY) / step), rows)
  let u = 0
  if (face === "north" || face === "south") {
    u = clampIndex(Math.floor((point.x - (cx - sectorM / 2)) / step), cols)
  } else {
    u = clampIndex(Math.floor((point.z - (cz - sectorM / 2)) / step), cols)
  }
  return { u, v }
}

/** Place a UV tile so its local XY lies in the face plane (thin axis = outward normal). */
function placeUvTile(
  tile: THREE.Object3D,
  face: CellFace,
  cx: number,
  cz: number,
  total: number,
  sectorM: number,
  u: number,
  v: number,
  outward: number,
  baseY: number,
) {
  const step = face === "top" || face === "bottom" ? sectorM / FACE_GRID : sectorM
  const uOff = (u + 0.5) * step - sectorM / 2
  const vOff = baseY + (v + 0.5) * step
  if (face === "top") {
    tile.position.set(cx + uOff, baseY + total + outward, cz + (v + 0.5) * step - sectorM / 2)
    tile.rotation.set(-Math.PI / 2, 0, 0)
  } else if (face === "bottom") {
    tile.position.set(cx + uOff, baseY - outward, cz + (v + 0.5) * step - sectorM / 2)
    tile.rotation.set(Math.PI / 2, 0, 0)
  } else if (face === "north") {
    tile.position.set(cx + uOff, vOff, cz - sectorM / 2 - outward)
    tile.rotation.set(0, Math.PI, 0)
  } else if (face === "south") {
    tile.position.set(cx + uOff, vOff, cz + sectorM / 2 + outward)
    tile.rotation.set(0, 0, 0)
  } else if (face === "east") {
    tile.position.set(cx + sectorM / 2 + outward, vOff, cz + uOff)
    tile.rotation.set(0, -Math.PI / 2, 0)
  } else {
    tile.position.set(cx - sectorM / 2 - outward, vOff, cz + uOff)
    tile.rotation.set(0, Math.PI / 2, 0)
  }
}

function uvTileGeometry(step: number) {
  return new THREE.PlaneGeometry(step, step)
}

export type SelectionToolbarAnchor = {
  kind: "sector" | "face"
  /** Mid-top of selection bbox (faces). */
  top: THREE.Vector3
  /** Four top corners of selection AABB (sectors); empty for faces. */
  sectorCorners: THREE.Vector3[]
  /** First vertical face of the selection (face toolbar only). */
  face?: CellFace
}

export type PlatformLayer = {
  setState: (state: PlatformLayerState) => void
  /** Hot-update solid wall fill without TextureLoader / full rebuild (color drag preview). */
  patchSolidFaceColors: (faceKeys: ReadonlyArray<string>, hex: string) => void
  /** Hot-update selected wall rows (horizontal strips) without waiting for the layout effect. */
  patchWallTileColors: (tiles: ReadonlyArray<FaceTile>, hex: string | null) => void
  /** Drop solid fill on faces (preview clear / floor color reset) without full rebuild. */
  clearSolidFaceColors: (faceKeys: ReadonlyArray<string>) => void
  setFloorVisible: (visible: boolean) => void
  hitAt: (clientX: number, clientY: number) => PlatformHit | null
  sectorOnPlane: (clientX: number, clientY: number) => { x: number; z: number } | null
  previewSectors: (keys: Set<string>) => void
  previewOpenings: (keys: Set<string>) => void
  /** World anchors for floating extrude toolbar (sector top corners vs face top). */
  selectionToolbarAnchor: () => SelectionToolbarAnchor | null
  span: () => number
  applyThemeColors: (colors: { top: string; side: string; grid: string }) => void
  dispose: () => void
}

export function createPlatformLayer(
  scene: THREE.Scene,
  camera: THREE.Camera,
  canvas: HTMLCanvasElement,
  setDefaultFloorVisible: (visible: boolean) => void,
): PlatformLayer {
  const root = new THREE.Group()
  // Looked up by name for the dev-only light-palette measureReference() check — see engine.ts.
  root.name = "locationScenePlatformRoot"
  const cellsRoot = new THREE.Group()
  const holesRoot = new THREE.Group()
  const wallSurfacesRoot = new THREE.Group()
  const overlayRoot = new THREE.Group()
  root.add(cellsRoot)
  root.add(holesRoot)
  root.add(wallSurfacesRoot)
  root.add(overlayRoot)
  scene.add(root)

  const raycaster = new THREE.Raycaster()
  const pointer = new THREE.Vector2()
  const ground = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0)
  const hitPoint = new THREE.Vector3()
  const selectionBox = new THREE.Box3()
  const selectionPoint = new THREE.Vector3()
  const anchorTop = new THREE.Vector3()
  const sectorCornerA = new THREE.Vector3()
  const sectorCornerB = new THREE.Vector3()
  const sectorCornerC = new THREE.Vector3()
  const sectorCornerD = new THREE.Vector3()
  const sectorCornersScratch = [sectorCornerA, sectorCornerB, sectorCornerC, sectorCornerD]
  const tileProbe = new THREE.Object3D()
  const loader = new THREE.TextureLoader()
  const textureCache = new Map<string, THREE.Texture>()

  let current: PlatformLayerState | null = null
  let lastGeometry = ""
  let lastFaces = ""
  let lastSelection = ""
  let sectorM = 1
  let sectorGrid: THREE.Group | null = null
  const sectorMats = new Map<string, THREE.MeshStandardMaterial[]>()
  const openingMats = new Map<string, THREE.Material>()
  const cellHeights = new Map<string, number>()
  let previewTileKeys = new Set<string>()
  let lastPreviewSectorKeys = new Set<string>()
  let lastSelectedSectorKeys = new Set<string>()
  /** Wall face keys from the previous selection pass — used to clear stale voxel sides. */
  let lastWallFaceKeys: Set<string> | null = null
  let wallRunIndex: WallRunIndex | null = null
  /** Sector toolbar height: freeze on selection key so Raise/Lower does not move arrows. */
  let frozenSectorToolbarKey = ""
  let frozenSectorToolbarY: number | null = null
  const dayTheme = sceneThemeById("light")
  let themeTopColor = hexToNumber(dayTheme.blockTop)
  let themeSideColor = hexToNumber(dayTheme.blockSide)
  let themeGridColor = hexToNumber(dayTheme.blockGrid)

  // Shared resources — never dispose via mesh traverse.
  const unitBoxGeo = new THREE.BoxGeometry(1, 1, 1)
  const sharedIdleMat = new THREE.MeshStandardMaterial({
    color: themeTopColor,
    roughness: 0.9,
    metalness: 0,
  })
  const sharedSideMat = new THREE.MeshStandardMaterial({
    color: themeSideColor,
    roughness: 0.95,
    metalness: 0,
  })
  const sharedSelectedMat = new THREE.MeshStandardMaterial({
    color: CELL_SELECTED,
    roughness: TOP_ROUGHNESS,
    metalness: 0,
  })
  // Flat floor depth, more negative draws closer: paint and empty selection -1, sector grid -2, selection over paint -3.
  /** Opaque cap for an empty flat cell. Not sharedSelectedMat: that material also paints tall wall faces. */
  const sharedSelectedCapMat = new THREE.MeshStandardMaterial({
    color: CELL_SELECTED,
    roughness: TOP_ROUGHNESS,
    metalness: 0,
    polygonOffset: true,
    polygonOffsetFactor: -1,
    polygonOffsetUnits: -1,
  })
  /** Selection over a painted top or wall tile: one see-through plane, fill stays visible. */
  const sharedSelectedPaintMat = new THREE.MeshBasicMaterial({
    color: CELL_SELECTED,
    transparent: true,
    opacity: 0.45,
    depthWrite: false,
    polygonOffset: true,
    polygonOffsetFactor: -3,
    polygonOffsetUnits: -3,
  })
  /** Wall-sector overlay. Stronger offset than the wall decal (factor -1) so it draws in front. */
  const sharedSelectedOverlayMat = new THREE.MeshBasicMaterial({
    color: CELL_SELECTED,
    transparent: true,
    opacity: 0.45,
    depthWrite: false,
    side: THREE.DoubleSide,
    polygonOffset: true,
    polygonOffsetFactor: -4,
    polygonOffsetUnits: -4,
  })
  const selectPaintPlaneGeo = new THREE.PlaneGeometry(1, 1)
  // Each flat instance is one sector (sectorM x sectorM) but can run tens of meters tall — it is the
  // floor FOUNDATION (cellFloorDepth, platform.ts), not a cell you build on. The top face keeps the
  // same sectorM x sectorM aspect as a built cell's roof, so it takes the tile map exactly like
  // sharedIdleMat; the side/bottom faces are 1 x (very tall) and would alias the 40x40 map into
  // horizontal banding, so they stay flat-colored. BoxGeometry group order: +x -x +y -y +z -z.
  const flatFloorTopMat = new THREE.MeshStandardMaterial({
    color: themeTopColor,
    roughness: 0.9,
    metalness: 0,
  })
  const flatFloorSideMat = new THREE.MeshStandardMaterial({
    color: themeSideColor,
    roughness: 0.95,
    metalness: 0,
  })
  const flatFloorMats = [
    flatFloorSideMat,
    flatFloorSideMat,
    flatFloorTopMat,
    flatFloorSideMat,
    flatFloorSideMat,
    flatFloorSideMat,
  ]
  sharedIdleMat.userData = { base: CELL_IDLE, alive: true, shared: true }
  sharedSideMat.userData = { base: CELL_SIDE, alive: true, shared: true }
  sharedSelectedMat.userData = { base: CELL_SELECTED, alive: true, shared: true }
  sharedSelectedCapMat.userData = { base: CELL_SELECTED, alive: true, shared: true }
  sharedSelectedPaintMat.userData = { base: CELL_SELECTED, alive: true, shared: true }
  sharedSelectedOverlayMat.userData = { base: CELL_SELECTED, alive: true, shared: true }
  flatFloorTopMat.userData = { base: CELL_IDLE, alive: true, shared: true }
  flatFloorSideMat.userData = { base: CELL_IDLE, alive: true, shared: true }
  const flatDummy = new THREE.Object3D()
  let flatMesh: THREE.InstancedMesh | null = null
  const flatIndexByKey = new Map<string, number>()
  let flatKeys: string[] = []
  let flatOutline: THREE.LineSegments | null = null
  /** Thin orange caps on flat terrace tops — foundation instances stay idle. */
  const selectTopRoot = new THREE.Group()
  selectTopRoot.userData = { kind: "sectorSelectTops" }
  cellsRoot.add(selectTopRoot)
  const selectTopByKey = new Map<string, THREE.Mesh>()

  const clearSectorToolbarFreeze = () => {
    frozenSectorToolbarKey = ""
    frozenSectorToolbarY = null
  }

  const isSharedMaterial = (item: THREE.Material) => Boolean((item as THREE.MeshStandardMaterial).userData?.shared)

  const disposeObject = (node: THREE.Object3D, disposeTextures: boolean) => {
    node.traverse((child) => {
      const mesh = child as THREE.Mesh
      if (mesh.geometry && mesh.geometry !== unitBoxGeo && mesh.geometry !== selectPaintPlaneGeo) {
        mesh.geometry.dispose()
      }
      const mat = mesh.material
      const mats = Array.isArray(mat) ? mat : mat ? [mat] : []
      for (const item of mats) {
        if (isSharedMaterial(item)) continue
        const map = (item as THREE.MeshStandardMaterial).map
        if (map) {
          let cached = false
          for (const [url, texture] of textureCache) {
            if (texture === map) {
              cached = true
              if (disposeTextures) {
                textureCache.delete(url)
                map.dispose()
              }
              break
            }
          }
          // Wall UV slices clone from cache — always dispose clones.
          if (!cached) map.dispose()
        }
        item.dispose()
      }
    })
  }

  const clearGroup = (group: THREE.Group, disposeTextures: boolean) => {
    const doomed = [...group.children]
    for (const child of doomed) {
      group.remove(child)
      disposeObject(child, disposeTextures)
    }
  }

  const clearFlatMesh = () => {
    if (!flatMesh) return
    cellsRoot.remove(flatMesh)
    // Keep unitBoxGeo + flatFloorMats — do not call InstancedMesh.dispose().
    flatMesh = null
    flatIndexByKey.clear()
    flatKeys = []
  }

  const clearSelectTops = () => {
    for (const mesh of selectTopByKey.values()) {
      selectTopRoot.remove(mesh)
      // sharedSelectedMat — do not dispose
    }
    selectTopByKey.clear()
  }

  // Sector lines stay above painted caps (offset -1).
  const gridMajorMat = new THREE.MeshBasicMaterial({
    color: themeGridColor,
    transparent: true,
    opacity: 0.7,
    depthWrite: false,
    side: THREE.DoubleSide,
    polygonOffset: true,
    polygonOffsetFactor: -2,
    polygonOffsetUnits: -2,
  })
  const gridMinorMat = new THREE.MeshBasicMaterial({
    color: themeGridColor,
    transparent: true,
    opacity: 0.3,
    depthWrite: false,
    side: THREE.DoubleSide,
    polygonOffset: true,
    polygonOffsetFactor: -2,
    polygonOffsetUnits: -2,
  })
  gridMajorMat.userData = { shared: true }
  gridMinorMat.userData = { shared: true }

  const clearSectorGrid = () => {
    if (!sectorGrid) return
    root.remove(sectorGrid)
    sectorGrid.traverse((child) => {
      const mesh = child as THREE.Mesh
      if (mesh.geometry && mesh.geometry !== unitBoxGeo) mesh.geometry.dispose()
    })
    sectorGrid = null
  }

  const clearFlatOutline = () => {
    if (!flatOutline) return
    root.remove(flatOutline)
    flatOutline.geometry.dispose()
    const mat = flatOutline.material
    if (Array.isArray(mat)) mat.forEach((item) => item.dispose())
    else mat.dispose()
    flatOutline = null
  }

  const clearStructure = () => {
    sectorMats.clear()
    cellHeights.clear()
    wallRunIndex = null
    lastPreviewSectorKeys = new Set()
    lastSelectedSectorKeys = new Set()
    lastWallFaceKeys = null
    clearFlatMesh()
    clearSelectTops()
    cellsRoot.remove(selectTopRoot)
    clearSectorGrid()
    clearFlatOutline()
    clearGroup(cellsRoot, false)
    cellsRoot.add(selectTopRoot)
    clearGroup(holesRoot, false)
    clearGroup(wallSurfacesRoot, false)
    clearOverlay()
  }

  const clearOverlay = () => {
    openingMats.clear()
    clearGroup(overlayRoot, false)
  }

  const applyTexture = (material: THREE.MeshStandardMaterial, url: string) => {
    const applyMap = (base: THREE.Texture) => {
      if (!material.userData.alive) return
      material.map = base
      material.color.set(0xffffff)
      material.needsUpdate = true
    }
    const cached = textureCache.get(url)
    if (cached) {
      applyMap(cached)
      return
    }
    loader.load(url, (texture) => {
      texture.colorSpace = THREE.SRGBColorSpace
      textureCache.set(url, texture)
      applyMap(texture)
      applySelectionColors()
    })
  }

  const pickFaceUrl = (faceKey: string, faceByKey: Map<string, SceneFaceState>): string | null => {
    const face = faceByKey.get(faceKey)
    if (face?.solid_color) return null
    const url = face?.download_url || face?.url
    if (!url || face?.status === "pending") return null
    return url
  }

  const pickFaceSolid = (faceKey: string, faceByKey: Map<string, SceneFaceState>): string | null => {
    const face = faceByKey.get(faceKey)
    const solid = face?.solid_color?.trim()
    if (!solid || face?.status === "pending") return null
    return solid
  }

  const resolveFaceSolid = (key: string, faceByKey: Map<string, SceneFaceState>): string | null =>
    pickFaceSolid(key, faceByKey)

  const applySolidColor = (material: THREE.MeshStandardMaterial, hex: string) => {
    if (!material.userData.alive) return
    if (material.map) {
      const map = material.map
      let cached = false
      for (const texture of textureCache.values()) {
        if (texture === map) {
          cached = true
          break
        }
      }
      if (!cached) map.dispose()
      material.map = null
    }
    material.color.set(hex)
    // Selection deselect restores color from base — keep solid so re-select does not wipe paint.
    material.userData.base = hexToNumber(hex)
    material.needsUpdate = true
  }

  const resolveFaceUrl = (key: string, faceByKey: Map<string, SceneFaceState>): string | null => {
    if (resolveFaceSolid(key, faceByKey)) return null
    const platform = current?.platform
    const parsed = parseCellFaceKey(key)
    if (!parsed) return pickFaceUrl(key, faceByKey)
    if (parsed.face === "top" || parsed.face === "bottom" || !platform) {
      return pickFaceUrl(key, faceByKey)
    }
    const run = wallRunIndex?.byFaceKey.get(key) ?? null
    const keys = run ?? [key]
    const primaryUrl = keys[0] ? pickFaceUrl(keys[0], faceByKey) : null
    if (primaryUrl) return primaryUrl
    for (const faceKey of keys) {
      const url = pickFaceUrl(faceKey, faceByKey)
      if (url) return url
    }
    return null
  }

  const paint = (
    key: string,
    faceByKey: Map<string, SceneFaceState>,
    selectedNow: boolean,
    options?: { applyMap?: boolean },
  ) => {
    const parsed = parseCellFaceKey(key)
    const isWallSide = parsed != null && parsed.face !== "top" && parsed.face !== "bottom"
    const solid = resolveFaceSolid(key, faceByKey)
    const hasImage = Boolean(resolveFaceUrl(key, faceByKey))
    const wantMap =
      options?.applyMap === true || (options?.applyMap !== false && !isWallSide && (Boolean(solid) || hasImage))
    // Shared mats for plain wall/voxel faces — selection swaps sharedSelectedMat.
    if (!wantMap) {
      if (selectedNow) return sharedSelectedMat
      return isWallSide ? sharedSideMat : sharedIdleMat
    }
    const fallback = isWallSide ? themeSideColor : themeTopColor
    const material = new THREE.MeshStandardMaterial({
      color: selectedNow ? CELL_SELECTED : solid ? hexToNumber(solid) : fallback,
      roughness: isWallSide ? SIDE_ROUGHNESS : TOP_ROUGHNESS,
      metalness: 0,
    })
    material.userData = { faceKey: key, base: CELL_IDLE, alive: true, wallSide: isWallSide }
    if (solid) {
      applySolidColor(material, solid)
    } else {
      const url = resolveFaceUrl(key, faceByKey)
      if (url) applyTexture(material, url)
    }
    return material
  }

  const addBoxEdges = (mesh: THREE.Mesh) => {
    const geo = new THREE.EdgesGeometry(mesh.geometry)
    // Scale edges with mesh: geometry is unit box, so edges must match scaled mesh.
    const mat = new THREE.LineBasicMaterial({ color: themeGridColor })
    mesh.add(new THREE.LineSegments(geo, mat))
  }

  const pushRibbon = (positions: number[], x0: number, z0: number, x1: number, z1: number, y: number, half: number) => {
    const alongX = Math.abs(x1 - x0) >= Math.abs(z1 - z0)
    if (alongX) {
      positions.push(
        x0,
        y,
        z0 - half,
        x1,
        y,
        z0 - half,
        x1,
        y,
        z0 + half,
        x0,
        y,
        z0 - half,
        x1,
        y,
        z0 + half,
        x0,
        y,
        z0 + half,
      )
      return
    }
    positions.push(
      x0 - half,
      y,
      z0,
      x0 + half,
      y,
      z0,
      x0 + half,
      y,
      z1,
      x0 - half,
      y,
      z0,
      x0 + half,
      y,
      z1,
      x0 - half,
      y,
      z1,
    )
  }

  const ribbonMesh = (positions: number[], material: THREE.Material) => {
    const geometry = new THREE.BufferGeometry()
    geometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3))
    const mesh = new THREE.Mesh(geometry, material)
    mesh.renderOrder = 2
    mesh.raycast = () => {}
    mesh.castShadow = false
    mesh.receiveShadow = false
    mesh.userData = { kind: "sectorGrid" }
    return mesh
  }

  /** Two ribbon levels on the flat floor. Major every GRID_MAJOR_EVERY, edges always major. */
  const rebuildSectorGrid = (platform: ScenePlatform) => {
    clearSectorGrid()
    const nx = platform.sectors_x
    const nz = platform.sectors_z
    const originX = (-nx / 2) * sectorM
    const originZ = (-nz / 2) * sectorM
    const maxX = originX + nx * sectorM
    const maxZ = originZ + nz * sectorM
    const y = sectorGridY(sectorM)
    const minorHalf = (sectorM * GRID_MINOR_WIDTH_OF_CELL) / 2
    const majorHalf = minorHalf * 2
    const majorPositions: number[] = []
    const minorPositions: number[] = []
    const isMajor = (index: number, cells: number) =>
      index === 0 || index === cells || (GRID_MAJOR_EVERY < cells && index % GRID_MAJOR_EVERY === 0)
    for (let i = 0; i <= nx; i += 1) {
      const x = originX + i * sectorM
      const dest = isMajor(i, nx) ? majorPositions : minorPositions
      const half = isMajor(i, nx) ? majorHalf : minorHalf
      pushRibbon(dest, x, originZ, x, maxZ, y, half)
    }
    for (let j = 0; j <= nz; j += 1) {
      const z = originZ + j * sectorM
      const dest = isMajor(j, nz) ? majorPositions : minorPositions
      const half = isMajor(j, nz) ? majorHalf : minorHalf
      pushRibbon(dest, originX, z, maxX, z, y, half)
    }
    sectorGrid = new THREE.Group()
    sectorGrid.userData = { kind: "sectorGrid" }
    if (majorPositions.length > 0) sectorGrid.add(ribbonMesh(majorPositions, gridMajorMat))
    if (minorPositions.length > 0) sectorGrid.add(ribbonMesh(minorPositions, gridMinorMat))
    root.add(sectorGrid)
  }

  const paintSelectedOrSolid = (material: THREE.MeshStandardMaterial, selected: boolean) => {
    if (material.map) {
      const shared = Boolean((material.userData as { shared?: boolean }).shared)
      material.color.set(selected ? CELL_SELECTED : shared ? themeTopColor : 0xffffff)
      return
    }
    const base = (material.userData as { base?: number }).base ?? CELL_IDLE
    const wallSide = Boolean((material.userData as { wallSide?: boolean }).wallSide)
    if (selected && (base === CELL_IDLE || base === CELL_SIDE)) material.color.set(CELL_SELECTED)
    else if (base === CELL_SIDE || (wallSide && base === CELL_IDLE)) material.color.set(themeSideColor)
    else if (base === CELL_IDLE) material.color.set(themeTopColor)
    else material.color.set(base)
  }

  const cellTopHasMedia = (sectorKey: string) => {
    const [xs, zs] = sectorKey.split(":")
    const x = Number(xs)
    const z = Number(zs)
    if (!Number.isFinite(x) || !Number.isFinite(z) || !current) return false
    const face = current.faces.find((row) => row.face_key === cellFaceKey(x, z, "top"))
    if (!face || face.status === "pending") return false
    if (face.solid_color?.trim()) return true
    return Boolean(face.download_url || face.url)
  }

  const dropSelectTop = (key: string) => {
    const existing = selectTopByKey.get(key)
    if (!existing) return
    selectTopRoot.remove(existing)
    selectTopByKey.delete(key)
  }

  const setFlatTopHighlight = (key: string, on: boolean) => {
    if (!on) {
      dropSelectTop(key)
      return
    }
    const painted = cellTopHasMedia(key)
    const existing = selectTopByKey.get(key)
    if (existing && Boolean(existing.userData.painted) === painted) return
    if (existing) dropSelectTop(key)
    const platform = current?.platform
    if (!platform) return
    const [xs, zs] = key.split(":")
    const x = Number(xs)
    const z = Number(zs)
    if (!Number.isFinite(x) || !Number.isFinite(z)) return
    const height = cellHeights.get(key) ?? 0
    if (height > 0) return
    const cx = (x + 0.5 - platform.sectors_x / 2) * sectorM
    const cz = (z + 0.5 - platform.sectors_z / 2) * sectorM
    const capH = selectCapHeight(sectorM)
    const mesh = painted
      ? new THREE.Mesh(selectPaintPlaneGeo, sharedSelectedPaintMat)
      : new THREE.Mesh(unitBoxGeo, sharedSelectedCapMat)
    if (painted) {
      // Plane sits above the cap so it does not intersect the cap volume.
      mesh.rotation.x = -Math.PI / 2
      mesh.scale.set(sectorM * 0.998, sectorM * 0.998, 1)
      mesh.position.set(cx, capH + selectPaintLift(sectorM), cz)
      mesh.renderOrder = 3
    } else {
      mesh.scale.set(sectorM * 0.998, capH, sectorM * 0.998)
      mesh.position.set(cx, capH / 2 + selectCapLift(sectorM), cz)
    }
    mesh.raycast = () => {}
    mesh.castShadow = false
    mesh.receiveShadow = false
    mesh.userData = { kind: "sectorSelectTop", ignorePick: true, x, z, painted }
    selectTopRoot.add(mesh)
    selectTopByKey.set(key, mesh)
  }

  const selectedTileKeySet = () => {
    const keys = new Set<string>()
    for (const tile of current?.selectedFaceTiles ?? []) {
      keys.add(`${tile.faceKey}#${tile.u}:${tile.v}`)
    }
    return keys
  }

  const overlayFaceKeys = () => {
    const keys = new Set<string>()
    for (const tile of current?.selectedFaceTiles ?? []) keys.add(tile.faceKey)
    for (const key of previewTileKeys) {
      const sep = key.indexOf("#")
      if (sep > 0) keys.add(key.slice(0, sep))
    }
    return keys
  }

  const paintOverlayTile = () => sharedSelectedOverlayMat

  const setVoxelFaceMaterials = (key: string, x: number, z: number, on: boolean, wallFaceKeys: Set<string> | null) => {
    const faceSelected = (face: CellFace) => {
      const faceKey = cellFaceKey(x, z, face)
      const isFloor = face === "top" || face === "bottom"
      const faceOn = Boolean(wallFaceKeys?.has(faceKey))
      return faceOn || (on && isFloor)
    }
    const nextList: THREE.MeshStandardMaterial[] = []
    for (const child of cellsRoot.children) {
      if (child === flatMesh) continue
      const data = child.userData as { kind?: string; x?: number; z?: number }
      if (data.kind !== "cell" || `${data.x}:${data.z}` !== key) continue
      const mesh = child as THREE.Mesh
      const prevMats = Array.isArray(mesh.material)
        ? (mesh.material as THREE.MeshStandardMaterial[])
        : mesh.material
          ? [mesh.material as THREE.MeshStandardMaterial]
          : []
      // Thin carved floor slab — whole mesh is floor, not a wall column.
      const isFloorSlab = prevMats.length === 1 && !Array.isArray(mesh.material)
      if (isFloorSlab) {
        const selected = on
        const prev = prevMats[0]
        if (prev && isSharedMaterial(prev)) {
          mesh.material = selected ? sharedSelectedMat : sharedIdleMat
          nextList.push(selected ? sharedSelectedMat : sharedIdleMat)
        } else if (prev) {
          paintSelectedOrSolid(prev, selected)
          nextList.push(prev)
        }
        continue
      }
      const materialsForCube = FACE_ORDER.map((face, i) => {
        const selected = faceSelected(face)
        const prev = prevMats.length === FACE_ORDER.length ? prevMats[i] : undefined
        if (prev && !isSharedMaterial(prev)) {
          paintSelectedOrSolid(prev, selected)
          return prev
        }
        return selected ? sharedSelectedMat : face === "top" || face === "bottom" ? sharedIdleMat : sharedSideMat
      })
      mesh.material = materialsForCube
      nextList.push(...materialsForCube)
    }
    if (nextList.length > 0) sectorMats.set(key, nextList)
  }

  const applySectorHighlight = (key: string, on: boolean, wallFaceKeys: Set<string> | null) => {
    const height = cellHeights.get(key) ?? 0
    if (height <= 0) {
      setFlatTopHighlight(key, on)
      // Textured flat individual meshes still use sectorMats face paints.
      if (sectorMats.has(key)) {
        const [xs, zs] = key.split(":")
        const x = Number(xs)
        const z = Number(zs)
        if (Number.isFinite(x) && Number.isFinite(z)) setVoxelFaceMaterials(key, x, z, on, wallFaceKeys)
      }
      return
    }
    setFlatTopHighlight(key, false)
    const [xs, zs] = key.split(":")
    const x = Number(xs)
    const z = Number(zs)
    if (!Number.isFinite(x) || !Number.isFinite(z)) return
    setVoxelFaceMaterials(key, x, z, on, wallFaceKeys)
  }

  const applyWallSurfaceHighlight = (_wallFaceKeys: Set<string> | null) => {
    // Selected sector is the overlay quad. Do not fade the whole painted run.
  }

  const applySelectionColors = () => {
    if (!current) return
    const sectorKeys = new Set(current.selectedSectors.map((sector) => `${sector.x}:${sector.z}`))
    const wallFaceKeys =
      !current.surfaceGrid && current.selectedFaceTiles.length > 0
        ? new Set(current.selectedFaceTiles.map((tile) => tile.faceKey))
        : null
    const preview = lastPreviewSectorKeys
    const nextOn = new Set<string>([...sectorKeys, ...preview])
    const prevOn = new Set<string>([...lastSelectedSectorKeys, ...lastPreviewSectorKeys])
    for (const key of prevOn) {
      if (!nextOn.has(key)) applySectorHighlight(key, false, wallFaceKeys)
    }
    for (const key of nextOn) {
      applySectorHighlight(key, true, wallFaceKeys)
    }
    // Wall face selection may highlight without sector key change
    if (wallFaceKeys) {
      for (const key of sectorMats.keys())
        applySectorHighlight(key, sectorKeys.has(key) || preview.has(key), wallFaceKeys)
      lastWallFaceKeys = wallFaceKeys
    } else if (lastWallFaceKeys && lastWallFaceKeys.size > 0) {
      // Leaving wall: side faces were painted via wallFaceKeys while sectorKeys was empty,
      // so prevOn/nextOn alone never touch those voxels — clear them explicitly.
      const cellsToRefresh = new Set<string>()
      for (const faceKey of lastWallFaceKeys) {
        const parsed = parseCellFaceKey(faceKey)
        if (parsed) cellsToRefresh.add(`${parsed.x}:${parsed.z}`)
      }
      for (const key of cellsToRefresh) {
        applySectorHighlight(key, sectorKeys.has(key) || preview.has(key), null)
      }
      lastWallFaceKeys = null
    }
    applyWallSurfaceHighlight(wallFaceKeys)
    lastSelectedSectorKeys = sectorKeys
    const selectedKeys = selectedTileKeySet()
    for (const child of overlayRoot.children) {
      if (!(child instanceof THREE.Mesh)) continue
      const data = child.userData as { faceKey?: string; u?: number; v?: number }
      const key = `${String(data.faceKey ?? "")}#${String(data.u)}:${String(data.v)}`
      child.visible = selectedKeys.has(key) || previewTileKeys.has(key)
    }
  }

  const addOverlayFace = (faceKey: string, selectedKeys: Set<string>, depthByTile: Map<string, number>) => {
    const platform = current?.platform
    if (!platform || !current) return
    const parsed = parseCellFaceKey(faceKey)
    if (!parsed) return
    const { x, z, face } = parsed
    if (face === "top" || face === "bottom") return
    if (x < 0 || z < 0 || x >= platform.sectors_x || z >= platform.sectors_z) return
    const height = cellHeights.get(`${x}:${z}`) ?? 0
    const span = Math.max(platform.sectors_x, platform.sectors_z) * sectorM
    const total = cellWorldHeight(height, sectorM, span)
    const baseY = cellStackBaseY(height, sectorM, span)
    const cx = (x + 0.5 - platform.sectors_x / 2) * sectorM
    const cz = (z + 0.5 - platform.sectors_z / 2) * sectorM
    const { cols, rows, step } = faceTileGrid(total, sectorM)
    if (rows <= 0) return
    for (let v = 0; v < rows; v += 1) {
      for (let u = 0; u < cols; u += 1) {
        const key = `${faceKey}#${u}:${v}`
        if (!selectedKeys.has(key) && !previewTileKeys.has(key)) continue
        const depth = depthByTile.get(`${faceKey}:${u}:${v}`) ?? 0
        if (depth > 0) {
          const opening = { face_key: faceKey, u, v, depth }
          if (openingDigThrough(opening, current.cells, platform)) continue
          const length = digTunnelLength(faceKey, v, current.cells, platform)
          const outward = -Math.min(depth, length) * sectorM + wallOverlayGap(sectorM)
          const material = paintOverlayTile()
          const tile = new THREE.Mesh(uvTileGeometry(step), material)
          placeUvTile(tile, face, cx, cz, total, sectorM, u, v, outward, baseY)
          tile.renderOrder = 3
          tile.castShadow = false
          tile.receiveShadow = false
          tile.userData = { kind: "opening", faceKey, u, v }
          overlayRoot.add(tile)
          openingMats.set(key, material)
          continue
        }
        const outward = -depth * sectorM + wallOverlayGap(sectorM)
        const material = paintOverlayTile()
        const tile = new THREE.Mesh(uvTileGeometry(step), material)
        placeUvTile(tile, face, cx, cz, total, sectorM, u, v, outward, baseY)
        tile.renderOrder = 3
        tile.castShadow = false
        tile.receiveShadow = false
        tile.userData = { kind: "opening", faceKey, u, v }
        overlayRoot.add(tile)
        openingMats.set(key, material)
      }
    }
  }

  const rebuildOverlay = () => {
    clearOverlay()
    if (!current?.platform) return
    // Surface grid: per-tile paint preview. Wall mode: face planes at opening depth when selected.
    if (!current.surfaceGrid && current.selectedFaceTiles.length === 0 && previewTileKeys.size === 0) return
    const selectedKeys = selectedTileKeySet()
    const depthByTile = new Map<string, number>()
    for (const opening of current.openings) {
      depthByTile.set(`${opening.face_key}:${opening.u}:${opening.v}`, opening.depth)
    }
    for (const faceKey of overlayFaceKeys()) addOverlayFace(faceKey, selectedKeys, depthByTile)
  }

  const addOpeningMesh = (opening: SceneOpening, platform: ScenePlatform, faceByKey: Map<string, SceneFaceState>) => {
    const match = opening.face_key.match(/^cell:(\d+):(\d+):(top|bottom|north|south|east|west)$/)
    if (!match) return
    const sx = Number(match[1])
    const sz = Number(match[2])
    const face = match[3] as CellFace
    if (face === "top" || face === "bottom") return
    const abs = Math.abs(opening.depth)
    if (abs <= 0.000001) return
    const height = cellHeights.get(`${sx}:${sz}`) ?? 0
    const span = Math.max(platform.sectors_x, platform.sectors_z) * sectorM
    const total = cellWorldHeight(height, sectorM, span)
    const baseY = cellStackBaseY(height, sectorM, span)
    const cx = (sx + 0.5 - platform.sectors_x / 2) * sectorM
    const cz = (sz + 0.5 - platform.sectors_z / 2) * sectorM
    const step = sectorM

    if (opening.depth < 0) {
      const thickness = abs * sectorM
      const material = paint(opening.face_key, faceByKey, false, { applyMap: true })
      const box = new THREE.Mesh(new THREE.BoxGeometry(step, step, Math.max(thickness, 0.02)), material)
      placeUvTile(box, face, cx, cz, total, sectorM, opening.u, opening.v, thickness / 2, baseY)
      box.castShadow = true
      box.receiveShadow = true
      box.userData = { kind: "opening", faceKey: opening.face_key, u: opening.u, v: opening.v }
      holesRoot.add(box)
      addBoxEdges(box)
      return
    }

    // Through dig: no far face and no pick ghost in empty air.
    if (current && openingDigThrough(opening, current.cells, platform)) return

    const pickMat = new THREE.MeshBasicMaterial({
      transparent: true,
      opacity: 0.01,
      depthWrite: false,
    })
    const pick = new THREE.Mesh(new THREE.BoxGeometry(step, step, 0.04), pickMat)
    placeUvTile(pick, face, cx, cz, total, sectorM, opening.u, opening.v, -0.02, baseY)
    pick.castShadow = false
    pick.receiveShadow = false
    pick.userData = { kind: "opening", faceKey: opening.face_key, u: opening.u, v: opening.v }
    holesRoot.add(pick)
  }

  const addVoxelColumn = (
    x: number,
    z: number,
    total: number,
    baseY: number,
    cx: number,
    cz: number,
    faceByKey: Map<string, SceneFaceState>,
    sectorOn: boolean,
    carved: Set<string>,
    _span: number,
  ) => {
    const { rows, step } = faceTileGrid(total, sectorM)
    const materialsForCell: THREE.MeshStandardMaterial[] = []
    for (let v = 0; v < rows; v += 1) {
      if (carved.has(carvedVoxelKey(x, z, v))) continue
      // Sector select = top/bottom only; vertical faces stay idle unless wall-selected later.
      const materialsForCube = FACE_ORDER.map((face) => {
        const isFloor = face === "top" || face === "bottom"
        return paint(cellFaceKey(x, z, face), faceByKey, sectorOn && isFloor)
      })
      materialsForCell.push(...materialsForCube)
      const mesh = new THREE.Mesh(unitBoxGeo, materialsForCube)
      mesh.castShadow = true
      mesh.receiveShadow = true
      mesh.scale.set(sectorM, step, sectorM)
      mesh.position.set(cx, baseY + (v + 0.5) * step, cz)
      mesh.userData = { kind: "cell", x, z, total, baseY }
      addBoxEdges(mesh)
      cellsRoot.add(mesh)
    }
    // Foundation under raised columns comes from rebuildFlatInstances (all sectors).
    sectorMats.set(`${x}:${z}`, materialsForCell)
  }

  const rowColor = (face: SceneFaceState | undefined, v: number): string | null => {
    if (!face || face.status === "pending") return null
    const map = face.tile_colors
    if (map && Object.keys(map).length > 0) {
      const color = map[`0:${v}`]?.trim()
      return color || null
    }
    const solid = face.solid_color?.trim()
    return solid || null
  }

  const addWallSurfaceMesh = (
    keys: readonly string[],
    data: { positions: number[]; uvs: number[]; indices: number[] },
    solid: string | null,
    url: string | null,
    row?: number,
  ) => {
    const primary = keys[0]
    if (!primary) return
    const geometry = new THREE.BufferGeometry()
    geometry.setAttribute("position", new THREE.Float32BufferAttribute(data.positions, 3))
    geometry.setAttribute("uv", new THREE.Float32BufferAttribute(data.uvs, 2))
    geometry.setIndex(data.indices)
    geometry.computeVertexNormals()
    const material = new THREE.MeshStandardMaterial({
      color: solid ? hexToNumber(solid) : 0xffffff,
      roughness: SIDE_ROUGHNESS,
      metalness: 0,
      polygonOffset: true,
      polygonOffsetFactor: -1,
      polygonOffsetUnits: -1,
    })
    const runId = row == null ? keys.join(",") : `${keys.join(",")}#${row}`
    material.userData = { alive: true, primaryFaceKey: primary, runId }
    const mesh = new THREE.Mesh(geometry, material)
    mesh.raycast = () => {}
    mesh.castShadow = true
    mesh.receiveShadow = true
    mesh.userData = { kind: "wallSurface", primaryFaceKey: primary, runId }
    wallSurfacesRoot.add(mesh)
    if (solid) applySolidColor(material, solid)
    else if (url) applyTexture(material, url)
  }

  const rebuildWallSurfaces = (
    state: PlatformLayerState,
    faceByKey: Map<string, SceneFaceState>,
    carved: Set<string>,
  ) => {
    clearGroup(wallSurfacesRoot, false)
    const platform = state.platform
    if (!platform) return
    const index = wallRunIndex ?? buildWallRunIndex(state.cells, platform)
    wallRunIndex = index
    for (const run of index.runs) {
      if (run.length === 0) continue
      let maxRows = 0
      let runUrl: string | null = null
      for (const key of run) {
        maxRows = Math.max(maxRows, wallFaceRowCount(key, state.cells, platform))
        if (!runUrl) runUrl = pickFaceUrl(key, faceByKey)
      }
      const coloredTiles = new Set<string>()
      for (let v = 0; v < maxRows; v += 1) {
        let indexInRun = 0
        while (indexInRun < run.length) {
          const solid = rowColor(faceByKey.get(run[indexInRun] ?? ""), v)
          if (!solid) {
            indexInRun += 1
            continue
          }
          let end = indexInRun + 1
          while (end < run.length && rowColor(faceByKey.get(run[end] ?? ""), v) === solid) end += 1
          const segment = run.slice(indexInRun, end)
          for (const key of segment) coloredTiles.add(`${key}:${v}`)
          const data = wallRunSurfaceMeshData({
            runFaceKeys: segment,
            cells: state.cells,
            platform,
            carved,
            onlyV: v,
          })
          if (data) addWallSurfaceMesh(segment, data, solid, null, v)
          indexInRun = end
        }
      }
      if (!runUrl) continue
      const data = wallRunSurfaceMeshData({
        runFaceKeys: run,
        cells: state.cells,
        platform,
        carved,
        omitTiles: coloredTiles,
      })
      if (data) addWallSurfaceMesh(run, data, null, runUrl)
    }
  }

  const flatCellHasFaceMedia = (x: number, z: number, faceByKey: Map<string, SceneFaceState>) =>
    Boolean(
      resolveFaceSolid(cellFaceKey(x, z, "top"), faceByKey) ||
      resolveFaceSolid(cellFaceKey(x, z, "bottom"), faceByKey) ||
      resolveFaceUrl(cellFaceKey(x, z, "top"), faceByKey) ||
      resolveFaceUrl(cellFaceKey(x, z, "bottom"), faceByKey),
    )

  /** Promote a flat (height ≤ 0) sector to an individual top/bottom cap mesh so solid/texture paint is visible. */
  const promoteFlatCellCap = (
    x: number,
    z: number,
    faceByKey: Map<string, SceneFaceState>,
    sectorKeys: Set<string>,
    depth: number,
  ) => {
    if (!current?.platform) return
    const key = `${x}:${z}`
    if (sectorMats.has(key)) return
    if ((cellHeights.get(key) ?? 0) > 0) return
    const platform = current.platform
    const cx = (x + 0.5 - platform.sectors_x / 2) * sectorM
    const cz = (z + 0.5 - platform.sectors_z / 2) * sectorM
    const sectorOn = sectorKeys.has(key)
    const materialsForCell = FACE_ORDER.map((face) => {
      const isFloor = face === "top" || face === "bottom"
      return paint(cellFaceKey(x, z, face), faceByKey, sectorOn && isFloor)
    })
    // Shared side materials also paint walls, so the depth shift stays on this cap's own materials.
    for (const material of materialsForCell) {
      if (isSharedMaterial(material)) continue
      material.polygonOffset = true
      material.polygonOffsetFactor = -1
      material.polygonOffsetUnits = -1
    }
    // Thin textured cap — volume comes from the shared foundation instance.
    const capH = selectCapHeight(sectorM)
    const mesh = new THREE.Mesh(unitBoxGeo, materialsForCell)
    // Receive the lamp shadow. Do not cast: a 2.5 cm cap draws a dark edge on the grey floor and neighbors.
    mesh.castShadow = false
    mesh.receiveShadow = true
    mesh.scale.set(sectorM, capH, sectorM)
    mesh.position.set(cx, capH / 2 + selectCapSeat(sectorM), cz)
    mesh.userData = { kind: "cell", x, z, total: depth, baseY: -depth }
    addBoxEdges(mesh)
    cellsRoot.add(mesh)
    sectorMats.set(key, materialsForCell)
  }

  /** Raised column tops use a shared material, so a solid fill has to replace that face. */
  const repaintRaisedCap = (
    x: number,
    z: number,
    face: "top" | "bottom",
    faceByKey: Map<string, SceneFaceState>,
  ): boolean => {
    if ((cellHeights.get(`${x}:${z}`) ?? 0) <= 0) return false
    let target: THREE.Mesh | null = null
    let targetY = face === "top" ? Number.NEGATIVE_INFINITY : Number.POSITIVE_INFINITY
    for (const child of cellsRoot.children) {
      const data = child.userData as { kind?: string; x?: number; z?: number }
      if (data.kind !== "cell" || data.x !== x || data.z !== z) continue
      const mesh = child as THREE.Mesh
      if (!Array.isArray(mesh.material) || mesh.material.length !== FACE_ORDER.length) continue
      const y = mesh.position.y
      if (face === "top" ? y > targetY : y < targetY) {
        targetY = y
        target = mesh
      }
    }
    if (!target || !Array.isArray(target.material)) return false
    const materials = [...(target.material as THREE.MeshStandardMaterial[])]
    const index = FACE_ORDER.indexOf(face)
    const prev = materials[index]
    const sectorOn = current?.selectedSectors.some((sector) => sector.x === x && sector.z === z) ?? false
    const next = paint(cellFaceKey(x, z, face), faceByKey, sectorOn)
    if (prev && prev !== next && !isSharedMaterial(prev)) {
      prev.userData.alive = false
      prev.dispose()
    }
    materials[index] = next
    target.material = materials
    return true
  }

  /** Drop a painted flat cap once the sector has neither a solid fill nor a texture. */
  const demoteFlatCellCap = (x: number, z: number) => {
    const key = `${x}:${z}`
    if (!sectorMats.has(key)) return
    if ((cellHeights.get(key) ?? 0) > 0) return
    const doomed: THREE.Object3D[] = []
    for (const child of cellsRoot.children) {
      const data = child.userData as { kind?: string; x?: number; z?: number }
      if (data.kind === "cell" && data.x === x && data.z === z) doomed.push(child)
    }
    for (const child of doomed) {
      cellsRoot.remove(child)
      disposeObject(child, false)
    }
    sectorMats.delete(key)
  }

  const rebuildFlatInstances = (
    platform: ScenePlatform,
    span: number,
    sectorKeys: Set<string>,
    faceByKey: Map<string, SceneFaceState>,
  ) => {
    const depth = cellFloorDepth(sectorM, span)
    const flats: { x: number; z: number; cx: number; cz: number }[] = []
    for (let z = 0; z < platform.sectors_z; z += 1) {
      for (let x = 0; x < platform.sectors_x; x += 1) {
        const height = cellHeights.get(`${x}:${z}`) ?? 0
        const cx = (x + 0.5 - platform.sectors_x / 2) * sectorM
        const cz = (z + 0.5 - platform.sectors_z / 2) * sectorM
        // Foundation under every sector (incl. raised) — no edge seam vs neighbors.
        flats.push({ x, z, cx, cz })
        // Textured/solid flat tops stay as individual meshes; foundation instance still below.
        if (height <= 0 && flatCellHasFaceMedia(x, z, faceByKey)) {
          promoteFlatCellCap(x, z, faceByKey, sectorKeys, depth)
        }
      }
    }
    if (flats.length === 0) return
    flatMesh = new THREE.InstancedMesh(unitBoxGeo, flatFloorMats, flats.length)
    flatMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage)
    flatMesh.receiveShadow = true
    flatMesh.castShadow = false
    flatMesh.userData = { kind: "flatFloor" }
    flatKeys = []
    flatIndexByKey.clear()
    for (let i = 0; i < flats.length; i += 1) {
      const item = flats[i]!
      const key = `${item.x}:${item.z}`
      flatKeys.push(key)
      flatIndexByKey.set(key, i)
      flatDummy.position.set(item.cx, -depth / 2, item.cz)
      flatDummy.scale.set(sectorM, depth, sectorM)
      flatDummy.updateMatrix()
      flatMesh.setMatrixAt(i, flatDummy.matrix)
      flatMesh.setColorAt(i, COLOR_IDLE)
    }
    flatMesh.instanceMatrix.needsUpdate = true
    if (flatMesh.instanceColor) flatMesh.instanceColor.needsUpdate = true
    cellsRoot.add(flatMesh)

    const boxGeo = new THREE.BoxGeometry(platform.sectors_x * sectorM, depth, platform.sectors_z * sectorM)
    const edgesGeo = new THREE.EdgesGeometry(boxGeo)
    boxGeo.dispose()
    const outlineMat = new THREE.LineBasicMaterial({ color: themeGridColor })
    flatOutline = new THREE.LineSegments(edgesGeo, outlineMat)
    flatOutline.position.set(0, -depth / 2, 0)
    flatOutline.raycast = () => {}
    flatOutline.userData = { kind: "flatOutline" }
    root.add(flatOutline)
  }

  const rebuildStructure = (state: PlatformLayerState) => {
    for (const materials of sectorMats.values()) {
      for (const material of materials) {
        if (!isSharedMaterial(material)) material.userData.alive = false
      }
    }
    clearStructure()
    const platform = state.platform
    if (!platform) {
      setDefaultFloorVisible(true)
      return
    }
    setDefaultFloorVisible(false)
    sectorM = sectorSizeMeters(platform)
    const span = Math.max(platform.sectors_x, platform.sectors_z) * sectorM
    for (const cell of state.cells) cellHeights.set(`${cell.x}:${cell.z}`, cell.height)
    const faceByKey = new Map(state.faces.map((face) => [face.face_key, face]))
    const sectorKeys = new Set(state.selectedSectors.map((sector) => `${sector.x}:${sector.z}`))
    lastSelectedSectorKeys = new Set(sectorKeys)
    const carved = carvedVoxels(state.openings, state.cells, platform)
    wallRunIndex = buildWallRunIndex(state.cells, platform)
    rebuildFlatInstances(platform, span, sectorKeys, faceByKey)
    rebuildSectorGrid(platform)
    for (let z = 0; z < platform.sectors_z; z += 1) {
      for (let x = 0; x < platform.sectors_x; x += 1) {
        const height = cellHeights.get(`${x}:${z}`) ?? 0
        if (height <= 0) continue
        const total = cellWorldHeight(height, sectorM, span)
        const baseY = cellStackBaseY(height, sectorM, span)
        const sectorOn = sectorKeys.has(`${x}:${z}`)
        const cx = (x + 0.5 - platform.sectors_x / 2) * sectorM
        const cz = (z + 0.5 - platform.sectors_z / 2) * sectorM
        addVoxelColumn(x, z, total, baseY, cx, cz, faceByKey, sectorOn, carved, span)
      }
    }
    for (const opening of state.openings) addOpeningMesh(opening, platform, faceByKey)
    rebuildWallSurfaces(state, faceByKey, carved)
    rebuildOverlay()
    applySelectionColors()
    // Walls dissolve into the sky's ground tone by height instead of scene.fog (wrong at this scale).
    const bounds = new THREE.Box3().setFromObject(root)
    applyHeightFade(sharedSideMat, LIGHT_PALETTE.ground, bounds.max.y - 0.2 * span, bounds.max.y - 3 * span)
  }

  const refreshFaceTextures = (state: PlatformLayerState) => {
    const faceByKey = new Map(state.faces.map((face) => [face.face_key, face]))
    const platform = state.platform
    const carved = platform ? carvedVoxels(state.openings, state.cells, platform) : new Set<string>()
    wallRunIndex = platform ? buildWallRunIndex(state.cells, platform) : null
    rebuildWallSurfaces(state, faceByKey, carved)

    if (platform) {
      const span = Math.max(platform.sectors_x, platform.sectors_z) * sectorM
      const depth = cellFloorDepth(sectorM, span)
      const sectorKeys = new Set(state.selectedSectors.map((sector) => `${sector.x}:${sector.z}`))
      for (let z = 0; z < platform.sectors_z; z += 1) {
        for (let x = 0; x < platform.sectors_x; x += 1) {
          if ((cellHeights.get(`${x}:${z}`) ?? 0) > 0) continue
          if (!flatCellHasFaceMedia(x, z, faceByKey)) {
            demoteFlatCellCap(x, z)
            continue
          }
          promoteFlatCellCap(x, z, faceByKey, sectorKeys, depth)
        }
      }
    }

    const refreshMaterial = (material: THREE.MeshStandardMaterial, faceKey: string) => {
      if (isSharedMaterial(material)) return
      if (!material.userData.alive) return
      const solid = resolveFaceSolid(faceKey, faceByKey)
      if (solid) {
        applySolidColor(material, solid)
        return
      }
      const url = resolveFaceUrl(faceKey, faceByKey)
      if (!url) {
        if (material.map) {
          const map = material.map
          let cached = false
          for (const texture of textureCache.values()) {
            if (texture === map) {
              cached = true
              break
            }
          }
          if (!cached) map.dispose()
          material.map = null
        }
        // Clear solid + texture: idle albedo and base so later deselect does not restore old solid.
        material.userData.base = CELL_IDLE
        material.color.set(CELL_IDLE)
        material.needsUpdate = true
        return
      }
      applyTexture(material, url)
    }

    for (const materials of sectorMats.values()) {
      for (const material of materials) {
        const faceKey = material.userData.faceKey as string | undefined
        if (!faceKey || material.userData.wallSide) continue
        const parsed = parseCellFaceKey(faceKey)
        if (!parsed || (parsed.face !== "top" && parsed.face !== "bottom")) continue
        refreshMaterial(material, faceKey)
      }
    }
    holesRoot.traverse((child) => {
      const mesh = child as THREE.Mesh
      const mat = mesh.material
      if (!mat || Array.isArray(mat)) return
      if (!(mat instanceof THREE.MeshStandardMaterial)) return
      const faceKey = mat.userData.faceKey ? String(mat.userData.faceKey) : null
      if (faceKey) refreshMaterial(mat, faceKey)
    })
    applySelectionColors()
  }

  const patchSolidFaceColors = (faceKeys: ReadonlyArray<string>, hex: string) => {
    if (!current || faceKeys.length === 0) return
    const keySet = new Set(faceKeys)
    const nextFaces = current.faces.map((face) =>
      keySet.has(face.face_key)
        ? {
            ...face,
            solid_color: hex,
            origin: face.origin ?? "upload",
            status: "ready" as const,
          }
        : face,
    )
    for (const faceKey of keySet) {
      if (nextFaces.some((face) => face.face_key === faceKey)) continue
      nextFaces.push({
        face_key: faceKey,
        origin: "upload",
        status: "ready",
        solid_color: hex,
      })
    }
    current = { ...current, faces: nextFaces }
    lastFaces = facesKey(current)

    let patched = 0
    const faceByKey = new Map(current.faces.map((face) => [face.face_key, face]))
    const platform = current.platform
    if (platform) {
      const span = Math.max(platform.sectors_x, platform.sectors_z) * sectorM
      const depth = cellFloorDepth(sectorM, span)
      const sectorKeys = new Set(current.selectedSectors.map((sector) => `${sector.x}:${sector.z}`))
      for (const faceKey of keySet) {
        const parsed = parseCellFaceKey(faceKey)
        if (!parsed || (parsed.face !== "top" && parsed.face !== "bottom")) continue
        promoteFlatCellCap(parsed.x, parsed.z, faceByKey, sectorKeys, depth)
        if (repaintRaisedCap(parsed.x, parsed.z, parsed.face, faceByKey)) patched += 1
      }
    }

    let rebuildWalls = false
    const recoloredWallKeys = new Set<string>()
    wallSurfacesRoot.traverse((child) => {
      const mesh = child as THREE.Mesh
      if (mesh.userData.kind !== "wallSurface") return
      const primary = String(mesh.userData.primaryFaceKey ?? "")
      const runId = String(mesh.userData.runId ?? "")
      const runKeys = runId ? runId.split(",") : [primary]
      if (!runKeys.some((key) => keySet.has(key))) return
      if (!runKeys.every((key) => keySet.has(key))) {
        rebuildWalls = true
        return
      }
      const mat = mesh.material
      if (mat instanceof THREE.MeshStandardMaterial) {
        applySolidColor(mat, hex)
        patched += 1
        for (const key of runKeys) recoloredWallKeys.add(key)
      }
    })
    if (!rebuildWalls) {
      for (const key of keySet) {
        const parsed = parseCellFaceKey(key)
        if (!parsed || parsed.face === "top" || parsed.face === "bottom") continue
        if (!recoloredWallKeys.has(key)) {
          rebuildWalls = true
          break
        }
      }
    }
    for (const materials of sectorMats.values()) {
      for (const material of materials) {
        const faceKey = material.userData.faceKey as string | undefined
        if (!faceKey || !keySet.has(faceKey)) continue
        if (isSharedMaterial(material)) continue
        applySolidColor(material, hex)
        patched += 1
      }
    }
    for (const [key, material] of openingMats) {
      const faceKey = key.split("#")[0]
      if (!faceKey || !keySet.has(faceKey) || isSharedMaterial(material)) continue
      if (!(material instanceof THREE.MeshStandardMaterial)) continue
      applySolidColor(material, hex)
      patched += 1
    }
    if ((rebuildWalls || patched === 0) && platform) {
      const carved = carvedVoxels(current.openings, current.cells, platform)
      wallRunIndex = buildWallRunIndex(current.cells, platform)
      rebuildWallSurfaces(current, faceByKey, carved)
    }
    applySelectionColors()
  }

  const patchWallTileColors = (tiles: ReadonlyArray<FaceTile>, hex: string | null) => {
    const platform = current?.platform
    if (!current || !platform || tiles.length === 0) return
    const faces = applyWallTileColors(current.faces, tiles, hex, current.cells, platform)
    current = { ...current, faces }
    lastFaces = facesKey(current)
    const faceByKey = new Map(faces.map((face) => [face.face_key, face]))
    const carved = carvedVoxels(current.openings, current.cells, platform)
    wallRunIndex = buildWallRunIndex(current.cells, platform)
    rebuildWallSurfaces(current, faceByKey, carved)
    applySelectionColors()
  }

  const clearSolidFaceColors = (faceKeys: ReadonlyArray<string>) => {
    if (!current || faceKeys.length === 0) return
    const keySet = new Set(faceKeys)
    const nextFaces = current.faces.map((face) => (keySet.has(face.face_key) ? { ...face, solid_color: null } : face))
    current = { ...current, faces: nextFaces }
    lastFaces = facesKey(current)
    refreshFaceTextures(current)
  }

  const setPointer = (clientX: number, clientY: number) => {
    const rect = canvas.getBoundingClientRect()
    pointer.x = ((clientX - rect.left) / Math.max(rect.width, 1)) * 2 - 1
    pointer.y = -((clientY - rect.top) / Math.max(rect.height, 1)) * 2 + 1
    raycaster.setFromCamera(pointer, camera)
  }

  return {
    setState(state) {
      const nextGeometry = geometryKey(state)
      const nextFaces = facesKey(state)
      const nextSelection = selectionKey(state)
      current = state
      if (nextGeometry !== lastGeometry) {
        lastGeometry = nextGeometry
        lastFaces = nextFaces
        lastSelection = nextSelection
        rebuildStructure(state)
        return
      }
      if (nextFaces !== lastFaces) {
        lastFaces = nextFaces
        refreshFaceTextures(state)
      }
      if (nextSelection !== lastSelection) {
        lastSelection = nextSelection
        rebuildOverlay()
        applySelectionColors()
      }
    },
    patchSolidFaceColors,
    patchWallTileColors,
    clearSolidFaceColors,
    setFloorVisible: setDefaultFloorVisible,
    hitAt(clientX, clientY) {
      if (!current?.platform) return null
      const platform = current.platform
      setPointer(clientX, clientY)
      const hits = raycaster.intersectObjects(root.children, true)
      for (const hit of hits) {
        let node: THREE.Object3D | null = hit.object
        while (node && node.parent !== root && !(node.userData as { kind?: string }).kind) node = node.parent
        if (!node) continue
        const data = node.userData as {
          kind?: string
          x?: number
          z?: number
          u?: number
          v?: number
          faceKey?: string
          total?: number
          baseY?: number
        }
        if (data.kind === "opening" && data.faceKey && data.u != null && data.v != null) {
          return { kind: "opening", faceKey: data.faceKey, u: data.u, v: data.v }
        }
        if (data.kind === "hole" && data.faceKey && data.u != null && data.v != null) {
          return { kind: "opening", faceKey: data.faceKey, u: data.u, v: data.v }
        }
        if (data.kind === "flatFloor" && hit.instanceId != null) {
          const key = flatKeys[hit.instanceId]
          if (!key) continue
          const [xs, zs] = key.split(":")
          const x = Number(xs)
          const z = Number(zs)
          if (!Number.isFinite(x) || !Number.isFinite(z)) continue
          const normal = hit.face
            ? hit.face.normal.clone().transformDirection(node.matrixWorld)
            : new THREE.Vector3(0, 1, 0)
          const face = faceFromNormal(normal)
          if (face === "top") return { kind: "sector", x, z, face }
          continue
        }
        if (!hit.face) continue
        const normal = hit.face.normal.clone().transformDirection(node.matrixWorld)
        if (data.kind === "cell" && data.x != null && data.z != null) {
          const face = faceFromNormal(normal)
          if (face === "top") return { kind: "sector", x: data.x, z: data.z, face }
          const height = cellHeights.get(`${data.x}:${data.z}`) ?? 0
          // Flat platform slab sides/bottom: not face-selectable (walls are height > 0).
          if (height <= 0) continue
          const faceKey = cellFaceKey(data.x, data.z, face)
          const span = Math.max(platform.sectors_x, platform.sectors_z) * sectorM
          const total = data.total ?? cellWorldHeight(height, sectorM, span)
          const baseY = data.baseY ?? cellStackBaseY(height, sectorM, span)
          const cx = (data.x + 0.5 - platform.sectors_x / 2) * sectorM
          const cz = (data.z + 0.5 - platform.sectors_z / 2) * sectorM
          const uv = uvFromPointOnFace(face, hit.point, cx, cz, total, sectorM, baseY)
          return { kind: "opening", faceKey, u: uv.u, v: uv.v }
        }
      }
      return null
    },
    sectorOnPlane(clientX, clientY) {
      const platform = current?.platform
      if (!platform) return null
      setPointer(clientX, clientY)
      const point = raycaster.ray.intersectPlane(ground, hitPoint)
      if (!point) return null
      const x = Math.floor(point.x / sectorM + platform.sectors_x / 2)
      const z = Math.floor(point.z / sectorM + platform.sectors_z / 2)
      if (x < 0 || z < 0 || x >= platform.sectors_x || z >= platform.sectors_z) return null
      return { x, z }
    },
    previewSectors(keys) {
      const sectorKeys = new Set(current?.selectedSectors.map((sector) => `${sector.x}:${sector.z}`) ?? [])
      const wallFaceKeys =
        current && !current.surfaceGrid && current.selectedFaceTiles.length > 0
          ? new Set(current.selectedFaceTiles.map((tile) => tile.faceKey))
          : null
      const prev = lastPreviewSectorKeys
      for (const key of prev) {
        if (!keys.has(key)) applySectorHighlight(key, sectorKeys.has(key), wallFaceKeys)
      }
      for (const key of keys) {
        if (!prev.has(key)) applySectorHighlight(key, true, wallFaceKeys)
      }
      lastPreviewSectorKeys = new Set(keys)
    },
    previewOpenings(keys) {
      previewTileKeys = keys
      rebuildOverlay()
    },
    selectionToolbarAnchor() {
      if (!current?.platform) {
        clearSectorToolbarFreeze()
        return null
      }
      const platform = current.platform
      const span = Math.max(platform.sectors_x, platform.sectors_z) * sectorM
      const pad = selectionToolbarPad(sectorM)
      selectionBox.makeEmpty()

      let kind: "sector" | "face" | null = null
      if (current.selectedSectors.length > 0) {
        kind = "sector"
        for (const sector of current.selectedSectors) {
          const height = cellHeights.get(`${sector.x}:${sector.z}`) ?? 0
          const total = cellWorldHeight(height, sectorM, span)
          const baseY = cellStackBaseY(height, sectorM, span)
          const cx = (sector.x + 0.5 - platform.sectors_x / 2) * sectorM
          const cz = (sector.z + 0.5 - platform.sectors_z / 2) * sectorM
          selectionBox.expandByPoint(selectionPoint.set(cx - sectorM / 2, baseY, cz - sectorM / 2))
          selectionBox.expandByPoint(selectionPoint.set(cx + sectorM / 2, baseY + total, cz + sectorM / 2))
        }
      } else if (current.selectedFaceTiles.length > 0) {
        clearSectorToolbarFreeze()
        kind = "face"
        for (const tile of current.selectedFaceTiles) {
          const parsed = parseCellFaceKey(tile.faceKey)
          if (!parsed || parsed.face === "top" || parsed.face === "bottom") continue
          const { x, z, face } = parsed
          const height = cellHeights.get(`${x}:${z}`) ?? 0
          const total = cellWorldHeight(height, sectorM, span)
          const baseY = cellStackBaseY(height, sectorM, span)
          const cx = (x + 0.5 - platform.sectors_x / 2) * sectorM
          const cz = (z + 0.5 - platform.sectors_z / 2) * sectorM
          placeUvTile(tileProbe, face, cx, cz, total, sectorM, tile.u, tile.v, 0.01, baseY)
          const step = sectorM
          selectionBox.expandByPoint(
            selectionPoint.set(
              tileProbe.position.x - step / 2,
              tileProbe.position.y - step / 2,
              tileProbe.position.z - step / 2,
            ),
          )
          selectionBox.expandByPoint(
            selectionPoint.set(
              tileProbe.position.x + step / 2,
              tileProbe.position.y + step / 2,
              tileProbe.position.z + step / 2,
            ),
          )
        }
      } else {
        clearSectorToolbarFreeze()
        return null
      }

      if (selectionBox.isEmpty() || kind == null) {
        clearSectorToolbarFreeze()
        return null
      }
      const midZ = (selectionBox.min.z + selectionBox.max.z) / 2
      const midX = (selectionBox.min.x + selectionBox.max.x) / 2
      const liveTopY = selectionBox.max.y + pad
      anchorTop.set(midX, liveTopY, midZ)
      if (kind === "sector") {
        const key = current.selectedSectors
          .map((sector) => `${sector.x}:${sector.z}`)
          .sort()
          .join(",")
        if (key !== frozenSectorToolbarKey || frozenSectorToolbarY == null) {
          frozenSectorToolbarKey = key
          frozenSectorToolbarY = liveTopY
        }
        const topY = frozenSectorToolbarY
        const { min, max } = selectionBox
        sectorCornerA.set(min.x, topY, min.z)
        sectorCornerB.set(min.x, topY, max.z)
        sectorCornerC.set(max.x, topY, min.z)
        sectorCornerD.set(max.x, topY, max.z)
        return { kind, top: anchorTop, sectorCorners: sectorCornersScratch }
      }
      let face: CellFace | undefined
      for (const tile of current.selectedFaceTiles) {
        const parsed = parseCellFaceKey(tile.faceKey)
        if (!parsed || parsed.face === "top" || parsed.face === "bottom") continue
        face = parsed.face
        break
      }
      return { kind, top: anchorTop, sectorCorners: [], face }
    },
    span() {
      const platform = current?.platform
      if (!platform) return 8
      return Math.max(platform.sectors_x, platform.sectors_z) * sectorSizeMeters(platform)
    },
    applyThemeColors(colors) {
      themeTopColor = hexToNumber(colors.top)
      themeSideColor = hexToNumber(colors.side)
      themeGridColor = hexToNumber(colors.grid)
      sharedIdleMat.color.set(themeTopColor)
      sharedIdleMat.roughness = 0.9
      sharedSideMat.color.set(themeSideColor)
      sharedSideMat.roughness = 0.95
      flatFloorTopMat.color.set(themeTopColor)
      flatFloorTopMat.roughness = 0.9
      flatFloorSideMat.color.set(themeSideColor)
      flatFloorSideMat.roughness = 0.95
      gridMajorMat.color.set(themeGridColor)
      gridMinorMat.color.set(themeGridColor)
      for (const materials of sectorMats.values()) {
        for (const material of materials) paintSelectedOrSolid(material, false)
      }
      root.traverse((child) => {
        const lines = child as THREE.LineSegments
        if (!lines.isLineSegments) return
        const mat = lines.material
        const mats = Array.isArray(mat) ? mat : [mat]
        for (const item of mats) {
          if (item instanceof THREE.LineBasicMaterial) item.color.set(themeGridColor)
        }
      })
      applySelectionColors()
    },
    dispose() {
      for (const materials of sectorMats.values()) {
        for (const material of materials) {
          if (!isSharedMaterial(material)) material.userData.alive = false
        }
      }
      clearStructure()
      unitBoxGeo.dispose()
      gridMajorMat.dispose()
      gridMinorMat.dispose()
      sharedIdleMat.dispose()
      sharedSideMat.dispose()
      sharedSelectedMat.dispose()
      sharedSelectedCapMat.dispose()
      sharedSelectedPaintMat.dispose()
      sharedSelectedOverlayMat.dispose()
      selectPaintPlaneGeo.dispose()
      flatFloorTopMat.dispose()
      flatFloorSideMat.dispose()
      for (const texture of textureCache.values()) texture.dispose()
      textureCache.clear()
      scene.remove(root)
      setDefaultFloorVisible(true)
    },
  }
}
